import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import LeftSidebar from "@/components/LeftSidebar";
import StatsBar from "@/components/StatsBar";
import ScostFilterBar from "@/components/ScostFilterBar";
import PropertiesTable from "@/components/PropertiesTable";
import PropertyDialog from "@/components/PropertyDialog";
import ImportDialog from "@/components/ImportDialog";
import MembersDialog from "@/components/MembersDialog";
import MapView from "@/components/MapView";
import PresenceIndicator from "@/components/PresenceIndicator";
import ExportMenu from "@/components/ExportMenu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Upload, Table as TableIcon, Map as MapIcon, LayoutGrid, MapPin, Users, ChevronLeft, Crown, Trash2, X, Lock } from "lucide-react";
import api from "@/lib/api";
import { computeDuplicates } from "@/lib/duplicates";
import { sortRows } from "@/lib/sortRows";
import { loadBoardPrefs, saveBoardPrefs } from "@/lib/boardPrefs";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const Dashboard = () => {
  const { user } = useAuth();
  const { boardId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusPropId = searchParams.get("focus");
  const [board, setBoard] = useState(null);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState([]);
  const [view, setView] = useState("table");
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [properties, setProperties] = useState([]);
  const [stats, setStats] = useState({ count: 0, avg_prezzo: 0, avg_eur_mq: 0, min_prezzo: 0, max_prezzo: 0, total_prezzo: 0, avg_mq: 0 });
  const [facets, setFacets] = useState({ citta: [], macro_area: [] });
  const [loading, setLoading] = useState(true);
  const [propertyDialogOpen, setPropertyDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [dupDismissTarget, setDupDismissTarget] = useState(null);
  const [dupDismissing, setDupDismissing] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [focusedProperty, setFocusedProperty] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Load persisted preferences on board change (once per boardId).
  useEffect(() => {
    setPrefsLoaded(false);
    if (!boardId) return;
    const p = loadBoardPrefs(boardId);
    if (p) {
      setFilters(p.filters || {});
      setSortConfig(Array.isArray(p.sortConfig) ? p.sortConfig : []);
      setView(p.view || "table");
    } else {
      setFilters({});
      setSortConfig([]);
      setView("table");
    }
    setSelectedIds(new Set());
    // Defer marking loaded so we don't immediately overwrite prefs with defaults.
    setTimeout(() => setPrefsLoaded(true), 0);
  }, [boardId]);

  // Persist prefs on change (only after the initial load has finished).
  useEffect(() => {
    if (!prefsLoaded || !boardId) return;
    saveBoardPrefs(boardId, { filters, sortConfig, view });
  }, [prefsLoaded, boardId, filters, sortConfig, view]);

  const queryParams = useMemo(() => {
    const q = { board_id: boardId };
    Object.entries(filters).forEach(([k, v]) => {
      if (k === "scost_bands") {
        if (Array.isArray(v) && v.length > 0) q.scost_bands = v.join(",");
      } else if (k === "macro_area") {
        if (Array.isArray(v) && v.length > 0) q.macro_area = v.join(",");
      } else if (k === "only_duplicates") {
        // client-side post-filter
      } else if (k === "favorites_only") {
        if (v) q.favorites_only = "true";
      } else if (v !== "" && v !== null && v !== undefined) {
        q[k] = v;
      }
    });
    return q;
  }, [filters, boardId]);

  const dupsMap = useMemo(() => computeDuplicates(properties), [properties]);
  const visibleProperties = useMemo(
    () => filters.only_duplicates ? properties.filter((p) => dupsMap.has(p.id)) : properties,
    [properties, dupsMap, filters.only_duplicates]
  );
  // Same order the table shows — reused for the export report.
  const sortedProperties = useMemo(() => sortRows(visibleProperties, sortConfig), [visibleProperties, sortConfig]);

  const loadBoard = useCallback(async () => {
    try {
      const r = await api.get(`/boards/${boardId}`);
      setBoard(r.data);
    } catch (e) {
      if (e.response?.status === 403) toast.error("Non hai accesso a questa scheda");
      else if (e.response?.status === 404) toast.error("Scheda non trovata");
      navigate("/boards", { replace: true });
    }
  }, [boardId, navigate]);

  const load = useCallback(async () => {
    if (!boardId) return;
    setLoading(true);
    try {
      const [pr, st, fc] = await Promise.all([
        api.get("/properties", { params: queryParams }),
        api.get("/properties/stats", { params: queryParams }),
        api.get("/properties/facets", { params: { board_id: boardId } }),
      ]);
      setProperties(pr.data);
      setStats(st.data);
      setFacets(fc.data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Errore caricamento dati");
    } finally {
      setLoading(false);
    }
  }, [queryParams, boardId]);

  useEffect(() => { loadBoard(); }, [loadBoard]);
  useEffect(() => { load(); }, [load]);

  // Deep-link: when we arrive with ?focus=<prop_id> (from the leaderboard on
  // the Boards page), auto-scroll to the row, promote view to "both" and
  // centre the map on the property. We consume the query param so a reload
  // doesn't re-trigger the focus.
  useEffect(() => {
    if (!focusPropId || properties.length === 0) return;
    const target = properties.find((p) => p.id === focusPropId);
    if (!target) return;
    setView((v) => (v === "table" ? "both" : v));
    setFocusedProperty({ ...target, _t: Date.now() });
    setTimeout(() => {
      const row = document.querySelector(`[data-testid="property-row-${target.id}"]`);
      if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
      else document.querySelector(".leaflet-container")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    // Flash-highlight the row for a couple seconds so the user's eye lands there.
    setTimeout(() => {
      const row = document.querySelector(`[data-testid="property-row-${target.id}"]`);
      if (row) {
        row.classList.add("ring-2", "ring-[#F59E0B]", "ring-inset");
        setTimeout(() => row.classList.remove("ring-2", "ring-[#F59E0B]", "ring-inset"), 2400);
      }
    }, 500);
    // Strip the param so it doesn't fire again on state changes.
    const next = new URLSearchParams(searchParams);
    next.delete("focus");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusPropId, properties]);

  // Prune stale selection when the underlying list changes (e.g. filters).
  const propIdsSig = properties.map((p) => p.id).join(",");
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const valid = new Set(properties.map((p) => p.id));
      const next = new Set();
      for (const id of prev) if (valid.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propIdsSig]);

  const isBoardOwner = board?.role_in_board === "owner";
  const isSysAdmin = user?.role === "admin";
  const canWrite = !!board?.can_write; // owner/admin/editor
  const isDemo = user && user.role !== "admin" && (user.status || "demo") === "demo";
  const isViewer = board?.role_in_board === "viewer";
  const filtersActive = Object.keys(queryParams).filter((k) => k !== "board_id").length > 0;

  const toggleSelected = (id, on) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  };
  const toggleSelectAll = (ids, on) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (on) ids.forEach((id) => next.add(id));
      else ids.forEach((id) => next.delete(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const confirmDismissDuplicate = async () => {
    if (!dupDismissTarget) return;
    setDupDismissing(true);
    try {
      await api.patch(`/properties/${dupDismissTarget.id}/not-duplicate`, { is_not_duplicate: true });
      toast.success("Immobile marcato come non duplicato");
      setDupDismissTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setDupDismissing(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/properties/${toDelete.id}`);
      toast.success("Immobile eliminato");
      setToDelete(null);
      load(); loadBoard();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  const doBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const r = await api.post("/properties/bulk-delete", { board_id: boardId, ids });
      toast.success(`${r.data.deleted} immobili eliminati`);
      clearSelection();
      setBulkConfirmOpen(false);
      load(); loadBoard();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione in blocco");
    } finally {
      setBulkDeleting(false);
    }
  };

  const geocodeMissing = async () => {
    setGeocoding(true);
    try {
      const r = await api.post(`/properties/geocode-missing?board_id=${boardId}`);
      toast.success(`Geocodifica: ${r.data.geocoded}/${r.data.processed} immobili`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore geocoding");
    } finally { setGeocoding(false); }
  };

  return (
    <div className="min-h-screen">
      <AppHeader />
      <div className="flex">
        <LeftSidebar
          boardId={boardId}
          isOwner={board?.role_in_board === "owner" || user?.role === "admin"}
          filters={filters}
          setFilters={setFilters}
          facets={facets}
          onReset={() => setFilters({})}
        />

        <main className="flex-1 p-4 lg:p-6 min-w-0 relative overflow-hidden">
          {/* Decorative map — top-right corner, 20% opacity with radial fade toward center */}
          <div
            aria-hidden="true"
            data-testid="dashboard-map-decor"
            className="pointer-events-none absolute top-0 right-0 h-[520px] w-[70%] max-w-[900px] z-0"
            style={{
              backgroundImage: "url('https://a.tile.openstreetmap.org/5/17/11.png')",
              backgroundSize: "cover",
              backgroundPosition: "top right",
              backgroundRepeat: "no-repeat",
              opacity: 0.2,
              WebkitMaskImage: "radial-gradient(ellipse 90% 90% at top right, #000 0%, #000 25%, transparent 78%)",
              maskImage: "radial-gradient(ellipse 90% 90% at top right, #000 0%, #000 25%, transparent 78%)",
            }}
          />
          <div className="relative z-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
            <div className="min-w-0">
              <Link to="/boards" className="text-[11px] uppercase tracking-[0.25em] text-slate-500 hover:text-[#007AFF] inline-flex items-center gap-1" data-testid="back-to-boards">
                <ChevronLeft className="w-3 h-3" /> Torna alle schede
              </Link>
              <div className="flex items-center gap-3 mt-1">
                <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tighter leading-none truncate">{board?.name || "…"}</h1>
                {board?.role_in_board === "owner" && (
                  <Badge className="rounded-full bg-[#12466F] text-white text-[9px] uppercase tracking-widest border-0 h-5">
                    <Crown className="w-2.5 h-2.5 mr-1" /> Owner
                  </Badge>
                )}
                {board?.role_in_board === "editor" && (
                  <Badge className="rounded-full bg-emerald-100 text-emerald-800 text-[9px] uppercase tracking-widest border-0 h-5">Editor</Badge>
                )}
                {board?.role_in_board === "viewer" && (
                  <Badge className="rounded-full bg-slate-200 text-slate-700 text-[9px] uppercase tracking-widest border-0 h-5">Sola lettura</Badge>
                )}
                {board?.role_in_board === "admin" && (
                  <Badge className="rounded-full bg-amber-100 text-amber-800 text-[9px] uppercase tracking-widest border-0 h-5">Admin</Badge>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-2">
                {board?.description || (filtersActive ? "Statistiche calcolate sul filtro corrente." : "Statistiche calcolate su tutti gli immobili della scheda.")}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" className="rounded-full h-9 border-slate-300 bg-white" onClick={() => setMembersOpen(true)} data-testid="btn-members">
                <Users className="w-4 h-4 mr-1.5" /> Collaboratori
                {board && <span className="ml-1.5 text-[11px] font-mono-num text-slate-500">{(board.member_ids?.length || 0) + 1}</span>}
              </Button>
              <PresenceIndicator boardId={boardId} currentUserId={user?.user_id} />
              <div className="ios-segmented" data-testid="view-toggle">
                <button className={view === "both" ? "active" : ""} onClick={() => setView("both")} data-testid="view-both"><LayoutGrid className="w-3.5 h-3.5 inline mr-1" />Entrambi</button>
                <button className={view === "map" ? "active" : ""} onClick={() => setView("map")} data-testid="view-map"><MapIcon className="w-3.5 h-3.5 inline mr-1" />Mappa</button>
                <button className={view === "table" ? "active" : ""} onClick={() => setView("table")} data-testid="view-table"><TableIcon className="w-3.5 h-3.5 inline mr-1" />Tabella</button>
              </div>
              <ExportMenu
                rows={sortedProperties}
                duplicates={dupsMap}
                filters={filters}
                sortConfig={sortConfig}
                stats={stats}
                boardName={board?.name || ""}
              />
              {canWrite && !isDemo && (
                <>
                  <Button variant="outline" className="rounded-full h-9 border-slate-300 bg-white" onClick={geocodeMissing} disabled={geocoding} data-testid="btn-geocode">
                    <MapPin className="w-4 h-4 mr-1.5" /> {geocoding ? "Geocoding…" : "Geocodifica"}
                  </Button>
                  <Button variant="outline" className="rounded-full h-9 border-slate-300 bg-white" onClick={() => setImportOpen(true)} data-testid="btn-import">
                    <Upload className="w-4 h-4 mr-1.5" /> Import
                  </Button>
                </>
              )}
              {canWrite && isDemo && (
                <Button variant="outline" className="rounded-full h-9 border-slate-300 bg-white text-slate-500" onClick={() => window.dispatchEvent(new CustomEvent("demo-locked", { detail: { feature: "Importa Excel / CSV" } }))} data-testid="btn-import-demo">
                  <Lock className="w-4 h-4 mr-1.5" /> Import
                </Button>
              )}
              {canWrite && (
                <Button
                  className="rounded-full h-9 bg-[#007AFF] hover:bg-[#0060df] text-white"
                  onClick={() => {
                    if (isDemo) { window.dispatchEvent(new CustomEvent("demo-locked", { detail: { feature: "Aggiungi immobile" } })); return; }
                    setEditing(null); setPropertyDialogOpen(true);
                  }}
                  data-testid="btn-add-property"
                >
                  {isDemo ? <Lock className="w-4 h-4 mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
                  Aggiungi immobile
                </Button>
              )}
              {isViewer && (
                <span className="text-[11px] text-slate-500 italic px-3">Modalità sola lettura — non puoi modificare gli immobili di questa scheda.</span>
              )}
            </div>
          </div>

          <StatsBar stats={stats} filtersActive={filtersActive} />

          <div className="mt-4">
            <ScostFilterBar
              selected={filters.scost_bands || []}
              onChange={(v) => setFilters((f) => ({ ...f, scost_bands: v }))}
            />
          </div>

          {(view === "both" || view === "map") && (
            <div className="mt-5"><MapView properties={visibleProperties} boardId={boardId} canWrite={canWrite} focused={focusedProperty} /></div>
          )}

          {(view === "both" || view === "table") && (
            <div className="mt-5">
              <PropertiesTable
                properties={visibleProperties}
                duplicates={dupsMap}
                loading={loading}
                canWrite={canWrite}
                sortConfig={sortConfig}
                onSortChange={setSortConfig}
                selectedIds={selectedIds}
                onToggleSelected={toggleSelected}
                onToggleSelectAll={toggleSelectAll}
                onEdit={(p) => { setEditing(p); setPropertyDialogOpen(true); }}
                onDelete={(p) => setToDelete(p)}
                onDismissDuplicate={(p) => setDupDismissTarget(p)}
                onToggleFavorite={async (p) => {
                  const next = !p.is_favorite;
                  setProperties((prev) => prev.map((x) => x.id === p.id ? { ...x, is_favorite: next } : x));
                  try {
                    await api.patch(`/properties/${p.id}/favorite`, { is_favorite: next });
                    toast.success(next ? "Aggiunto ai preferiti" : "Rimosso dai preferiti");
                    load();
                  } catch (e) {
                    setProperties((prev) => prev.map((x) => x.id === p.id ? { ...x, is_favorite: p.is_favorite } : x));
                    toast.error(e?.response?.data?.detail || "Errore aggiornamento preferito");
                  }
                }}
                onFocus={(p) => {
                  if (view === "table") setView("both");
                  setFocusedProperty({ ...p, _t: Date.now() });
                  setTimeout(() => {
                    document.querySelector(".leaflet-container")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 50);
                }}
              />
            </div>
          )}
          </div>
        </main>
      </div>

      {/* Sticky bulk-action bar — appears whenever ≥1 row is selected */}
      {canWrite && selectedIds.size > 0 && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 rounded-full bg-slate-900 text-white shadow-2xl px-4 py-2.5"
          data-testid="bulk-action-bar"
        >
          <span className="text-xs font-mono-num" data-testid="bulk-selection-count">
            {selectedIds.size} selezionat{selectedIds.size === 1 ? "o" : "i"}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full h-8 text-white hover:bg-white/10"
            onClick={clearSelection}
            data-testid="bulk-clear"
          >
            <X className="w-3.5 h-3.5 mr-1.5" /> Deseleziona
          </Button>
          <Button
            size="sm"
            className="rounded-full h-8 bg-rose-600 hover:bg-rose-700 text-white"
            onClick={() => setBulkConfirmOpen(true)}
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Elimina {selectedIds.size}
          </Button>
        </div>
      )}

      <PropertyDialog
        open={propertyDialogOpen}
        onOpenChange={(v) => { setPropertyDialogOpen(v); if (!v) setEditing(null); }}
        editing={editing}
        onSaved={() => { load(); loadBoard(); }}
        boardId={boardId}
        boardName={board?.name || ""}
        macroAreas={facets?.macro_area || []}
      />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={() => { load(); loadBoard(); }} boardId={boardId} />
      <MembersDialog open={membersOpen} onOpenChange={setMembersOpen} boardId={boardId} isOwner={isBoardOwner || isSysAdmin} onChanged={loadBoard} />

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent className="rounded-2xl border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina immobile</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare "{toDelete?.indirizzo}"? Questa azione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-rose-600 hover:bg-rose-700" onClick={confirmDelete} data-testid="confirm-delete">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!dupDismissTarget} onOpenChange={(v) => !v && setDupDismissTarget(null)}>
        <AlertDialogContent className="rounded-2xl border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi che NON è un duplicato?</AlertDialogTitle>
            <AlertDialogDescription>
              L'immobile <b>"{dupDismissTarget?.indirizzo}"</b> risulta simile ad altri annunci nella scheda.
              Se sei sicuro che si tratta di un immobile a sé (annuncio diverso, unità distinta), verrà escluso dal rilevamento duplicati
              e l'icona fiamma scomparirà. L'operazione è reversibile modificando l'immobile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" disabled={dupDismissing}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full bg-[#007AFF] hover:bg-[#0060df]"
              onClick={confirmDismissDuplicate}
              disabled={dupDismissing}
              data-testid="confirm-dismiss-duplicate"
            >
              {dupDismissing ? "Salvataggio…" : "Sì, non è un duplicato"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <AlertDialogContent className="rounded-2xl border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina in blocco</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare <b>{selectedIds.size} immobili</b>. Questa azione è irreversibile e verrà notificata ai collaboratori della scheda.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" disabled={bulkDeleting}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full bg-rose-600 hover:bg-rose-700"
              onClick={doBulkDelete}
              disabled={bulkDeleting}
              data-testid="confirm-bulk-delete"
            >
              {bulkDeleting ? "Eliminazione…" : `Elimina ${selectedIds.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Dashboard;
